import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS } from '../utils/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'super_admin';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('token');
    console.log('[AuthContext] Initializing auth, token exists:', !!token);
    
    if (token) {
      // Verify token with backend - use /verify endpoint for admin
      console.log('[AuthContext] Token found, calling', API_ENDPOINTS.VERIFY);
      fetch(API_ENDPOINTS.VERIFY, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        console.log('[AuthContext] /verify response status:', response.status);
        return response.json();
      })
      .then(data => {
        console.log('[AuthContext] /verify response data:', data);
        // Backend returns: { success: true, user: {...} }
        const userData = data.user || data.admin || data.data?.user || data.data?.admin;
        if (data.success && userData) {
          console.log('[AuthContext] ✅ User data received:', userData);
          setUser({
            id: userData.id || userData._id,
            email: userData.email,
            name: userData.name || userData.email,
            role: userData.role || 'admin'
          });
          console.log('[AuthContext] ✅ User state updated');
        } else {
          console.log('[AuthContext] ❌ Verification failed, removing token');
          localStorage.removeItem('token');
          setUser(null);
        }
      })
      .catch((error) => {
        console.error('[AuthContext] ❌ Verification error:', error);
        localStorage.removeItem('token');
        setUser(null);
      })
      .finally(() => {
        console.log('[AuthContext] ✅ Loading set to false');
        setIsLoading(false);
      });
    } else {
      console.log('[AuthContext] No token found');
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('Attempting login to:', API_ENDPOINTS.LOGIN);
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      // Log response for debugging
      if (!response.ok || !data.success) {
        console.error('Login failed:', {
          status: response.status,
          statusText: response.statusText,
          data: data
        });
      }

      if (data.success && data.token) {
        localStorage.setItem('token', data.token);
        const userData = data.admin || data.user || data.data?.admin || data.data?.user;
        if (userData) {
          setUser({
            id: userData.id || userData._id,
            email: userData.email,
            name: userData.name || userData.email,
            role: userData.role || 'admin'
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
